import type { CellObject, WorkBook } from "xlsx"
import { read, utils, write } from "xlsx"

import { tagsListToFilterPredicate } from "../../tags/utils"
import { MimeType } from "../../types/mimetype"
import type { QuestionAnswerLayoutForAnswering } from "../../types/sheets"
import type {
  DocumentFields,
  DocumentSheet,
  FileDocument,
  GoogleDriveDocument,
  WorkbookData,
} from "./state"

const NO_LOCATION_LAYOUT_NAME = "NO_LOCATION_LAYOUT"

const removeEmptyRowsFromEnd = (rows: string[][]): string[][] => {
  const minRows = 5

  if (rows.length === 0) {
    // Make sure to return at least one cell so it doesn't look horrible.
    // TODO(mgraczyk): Add minimum column and row count.
    return [[""]]
  }

  for (let i = rows.length - 1; i >= minRows; i--) {
    if (rows[i].some((cell) => cell.trim() !== "")) {
      return rows.slice(0, i + 1)
    }
  }

  return rows.slice(0, Math.min(minRows, rows.length))
}

// Returns a map of sheet names to rows of cells.
// The rows are trimmed of empty rows at the end.
//
// If extraEmptyCols is provided, it will add that many empty columns to the end.
export const getDataFromWorkbook = (
  workbook: WorkBook,
  extraEmptyCols = 0,
): WorkbookData => {
  const sheetRows: Record<string, string[][]> = {}

  const sheetNames = workbook.SheetNames.filter(
    (s) => !workbook.Workbook?.Sheets?.find((ws) => ws.name === s)?.Hidden,
  )
  for (const sheetName of sheetNames) {
    const worksheet = workbook.Sheets[sheetName]
    const range = utils.decode_range(worksheet["!ref"] ?? "")
    const thisSheetRows = []

    // Add extra empty columns so that the user
    // can add a column for the answer at the end.
    const maxCol = Math.min(range.e.c + extraEmptyCols, 40)

    for (
      let rowNum = range.s.r;
      rowNum <= range.e.r && rowNum < range.s.r + 200;
      rowNum++
    ) {
      const row = []
      for (let colNum = 0; colNum <= maxCol; colNum++) {
        const cellAddress = { c: colNum, r: rowNum }
        const cellRef = utils.encode_cell(cellAddress)

        // Try to use formatted text when available, fall back to raw if it's missing.
        const cell = worksheet[cellRef] as CellObject | undefined
        row.push(String(cell?.w ?? cell?.v ?? ""))
      }
      thisSheetRows.push(row)
    }

    sheetRows[sheetName] = removeEmptyRowsFromEnd(thisSheetRows)
  }

  return { sheetRows, sheetNames }
}

const fileReaderPromise = (
  blob: Blob,
): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      resolve(reader.result)
    }

    reader.onerror = reject

    reader.readAsArrayBuffer(blob)
  })
}

const convertArrayBufferIfNeeded = (
  data: string | ArrayBuffer,
): ArrayBuffer => {
  if (typeof data === "string") {
    const encoder = new TextEncoder()
    return encoder.encode(data).buffer
  }

  return data
}

export const readFileAsync = async (
  blob: Blob,
): Promise<ArrayBuffer | null> => {
  const maybeArrayBuffer = await fileReaderPromise(blob)
  if (maybeArrayBuffer === null) {
    return null
  }

  return convertArrayBufferIfNeeded(maybeArrayBuffer)
}

export const convertNumbersToXlsx = async (
  file: File,
): Promise<ArrayBuffer> => {
  // Read the Numbers file as ArrayBuffer
  const fileBuffer = await readFileAsync(file)
  if (!fileBuffer) {
    throw new Error("Failed to read Numbers file")
  }

  // Parse the Numbers file using SheetJS
  const workbook = read(fileBuffer, { type: "array" })

  // Convert it to XLSX format
  return write(workbook, { type: "array", bookType: "xlsx" }) as ArrayBuffer
}

export const getXlsxSheetNameFromGoogleWorksheet = (googleWorksheet: {
  properties: { title: string }
}): string => {
  // XLSX is more restrictive than sheets in sheet names.
  // https://support.microsoft.com/en-us/office/rename-a-worksheet-3f1f7148-ee83-404d-8ef0-9ff99fbad1f9
  const invalidCharsRegex = /:|\/|\\|\?|\*|\]|\[/g
  const maxLength = 31
  const title = googleWorksheet.properties.title
    .replace(invalidCharsRegex, "")
    .slice(0, maxLength)
  if (title === "History") {
    return "History (Sheet)"
  }
  // TODO(mgraczyk): Fix Begin or end with an apostrophe (')
  return title
}

// If the selection is valid, returns a partial layout.
// If not valid, returns null.
const convertUiAnswerFieldsToLayoutIndices = (
  fields: DocumentFields,
): Pick<
  QuestionAnswerLayoutForAnswering,
  | "answer_kind"
  | "primary_question_index"
  | "secondary_question_index"
  | "primary_answer_index"
  | "secondary_answer_index"
> | null => {
  if (fields.primaryQuestion.index === undefined) {
    // All layouts require a primary question.
    return null
  }

  if (
    fields.answerExplanation.index !== undefined &&
    fields.answerCategorical.index !== undefined
  ) {
    return {
      answer_kind: "CATEGORICAL_WITH_EXPLANATION",
      primary_question_index: fields.primaryQuestion.index,
      secondary_question_index: fields.secondaryQuestion.index,
      primary_answer_index: fields.answerCategorical.index,
      secondary_answer_index: fields.answerExplanation.index,
    }
  }

  if (fields.answerCategorical.index !== undefined) {
    return {
      answer_kind: "CATEGORICAL",
      primary_question_index: fields.primaryQuestion.index,
      secondary_question_index: fields.secondaryQuestion.index,
      primary_answer_index: fields.answerCategorical.index,
      secondary_answer_index: null,
    }
  }

  if (fields.answerExplanation.index !== undefined) {
    return {
      answer_kind: "SINGLE_TEXT",
      primary_question_index: fields.primaryQuestion.index,
      secondary_question_index: fields.secondaryQuestion.index,
      primary_answer_index: fields.answerExplanation.index,
      secondary_answer_index: null,
    }
  }

  return null
}

export const isValidSheet = (sheet: DocumentSheet): boolean => {
  return (
    // Whether or not it is verbatim from the answer bank does not affect
    // validity.
    convertUiAnswerFieldsToLayoutIndices(sheet.fields) != null
  )
}

export const getQuestionAnswerLayouts = (
  doc: FileDocument | GoogleDriveDocument,
): Record<string, QuestionAnswerLayoutForAnswering> => {
  const questionAnswerLayouts: Record<
    string,
    QuestionAnswerLayoutForAnswering
  > = {}

  const answerer_controls = (() => {
    const tagsFilter = tagsListToFilterPredicate(
      doc.selectedTags,
      doc.tagOperator,
    )
    const isDefaultLocale = doc.locale === "en_US"

    if (tagsFilter === null && isDefaultLocale) {
      return null
    }

    return {
      tags_filter: tagsFilter,
      response_locale: doc.locale,
    }
  })()

  const baseLayoutProps = {
    require_verbatim_kind: doc.verbatimFromAnswerBank
      ? ("ANSWER_BANK" as const)
      : null,
    answerer_controls,
  }

  if (doc.mimeType === MimeType.PDF || doc.mimeType === MimeType.DOCX) {
    questionAnswerLayouts[NO_LOCATION_LAYOUT_NAME] = {
      is_row: false,
      primary_question_index: -1,
      primary_answer_index: -1,
      answer_kind: "SINGLE_TEXT",
      ...baseLayoutProps,
    }
  } else {
    for (const { sheetName, fields, isRow, multipleChoices } of doc.sheets) {
      if (!sheetName) continue

      const maybeLayout = convertUiAnswerFieldsToLayoutIndices(fields)
      if (maybeLayout === null) continue

      questionAnswerLayouts[sheetName] = {
        is_row: isRow,
        question_id_index: fields.questionId.index,
        header_index: null,
        answer_categorical_options:
          multipleChoices.length > 0 ? multipleChoices : null,
        ...maybeLayout,
        ...baseLayoutProps,
      }
    }
  }

  if (Object.keys(questionAnswerLayouts).length === 0) {
    throw new Error("No valid sheets")
  }

  return questionAnswerLayouts
}
